// Stage Mail-V2 Stage 3 (2026-07-22): Conversation View hook。
//
// 调用 mailV2Api.getThread(messageId),返 { items, rootId, currentId, total,
// truncated, loading, error, refresh }。
//
// 行为:
// - selectedMessageId 变化 → 重新 fetch + cancel in-flight(同 useMailMessages 模式)
// - errors 不 throw,只是 setError,允许 UI render empty thread
// - demo mode (MAIL_V2_REAL=false):返 静态 single-item thread,不是 API 调用
//
// 入参 {messageId} 任意:null / undefined → 不 fetch,返空 thread state。

import { useCallback, useEffect, useState } from "react";
import { mailV2Api } from "../../../api/mail-v2";
import { mapMessage, type MessageV2View } from "../api/mappers";

type Args = {
  messageId: string | null | undefined;
};

type State = {
  items: MessageV2View[];
  rootId: string | null;
  currentId: string | null;
  total: number;
  truncated: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

const MAIL_V2_REAL = import.meta.env.VITE_MAIL_V2_REAL === "true";

export function useThread(args: Args): State {
  const [items, setItems] = useState<MessageV2View[]>([]);
  const [rootId, setRootId] = useState<string | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [truncated, setTruncated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    // No selected message → empty thread (no loading flicker).
    if (!args.messageId) {
      setItems([]);
      setRootId(null);
      setCurrentId(null);
      setTotal(0);
      setTruncated(false);
      setLoading(false);
      return;
    }

    // Demo mode: synthesize a single-item thread containing only the message
    // itself. Real mode is not selected, so we mirror the same shape.
    if (!MAIL_V2_REAL) {
      // Without a real message list to pull from, the demo thread is empty.
      // (Demo data is rendered by MailV2Page directly, no thread fetch.)
      setItems([]);
      setRootId(null);
      setCurrentId(null);
      setTotal(0);
      setTruncated(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const res = await mailV2Api.getThread(args.messageId as string);
        if (cancelled) return;
        setItems(res.items.map(mapMessage));
        setRootId(res.root_id);
        setCurrentId(res.current_id);
        setTotal(res.total);
        setTruncated(res.truncated);
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("[useThread] FAIL", {
            messageId: args.messageId,
            error: e instanceof Error ? e.message : String(e),
          });
          setError(e instanceof Error ? e.message : String(e));
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [args.messageId, reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return {
    items,
    rootId,
    currentId,
    total,
    truncated,
    loading,
    error,
    refresh,
  };
}
